import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Tag, Package } from 'lucide-react';
import { Button, Input, Card, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, useToast } from '@caruso/ui';

interface ProductCategory {
  id: string;
  name: string;
  description?: string;
  color?: string;
  productsCount: number;
}

export function ProductCategories() {
  const { toast } = useToast();
  const [categories, setCategories] = useState<ProductCategory[]>([
    { id: '1', name: 'Champagnes', description: 'Champagnes e espumantes premium', color: '#FFD700', productsCount: 14 },
    { id: '2', name: 'Vinhos', description: 'Vinhos tintos, brancos e rosés', color: '#8B0000', productsCount: 0 },
    { id: '3', name: 'Destilados', description: 'Whisky, vodka, gin e outros', color: '#CD853F', productsCount: 0 },
  ]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#FFD700',
  });

  const handleOpenDialog = (category?: ProductCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color || '#FFD700',
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        color: '#FFD700',
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      color: '#FFD700',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingCategory) {
      // Atualizar categoria existente
      setCategories(categories.map(cat =>
        cat.id === editingCategory.id
          ? { ...cat, ...formData }
          : cat
      ));
      toast({
        title: 'Categoria atualizada!',
        variant: 'success',
      });
    } else {
      // Criar nova categoria
      const newCategory: ProductCategory = {
        id: String(Date.now()),
        ...formData,
        productsCount: 0,
      };
      setCategories([...categories, newCategory]);
      toast({
        title: 'Categoria criada!',
        variant: 'success',
      });
    }

    handleCloseDialog();
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta categoria?')) {
      setCategories(categories.filter(cat => cat.id !== id));
      toast({
        title: 'Categoria excluída!',
        variant: 'success',
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Categorias de Produtos</h1>
          <p className="text-brand-ivory/60 mt-1">
            Organize seus produtos por categorias
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Nova Categoria
        </Button>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <Input
          placeholder="Buscar categorias..."
          className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
        />
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((category) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: category.color || '#FFD700' }}
                  />
                  <div>
                    <h3 className="font-serif text-brand-gold text-lg">
                      {category.name}
                    </h3>
                  </div>
                </div>
              </div>

              {category.description && (
                <p className="text-sm text-brand-ivory/60 mb-4">
                  {category.description}
                </p>
              )}

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-brand-ivory/60">Produtos:</span>
                  <span className="text-brand-ivory flex items-center gap-1">
                    <Package className="w-4 h-4" />
                    {category.productsCount}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-brand-gold/10">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleOpenDialog(category)}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(category.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {categories.length === 0 && (
        <div className="text-center py-12">
          <Tag className="w-16 h-16 text-brand-gold/40 mx-auto mb-4" />
          <p className="text-brand-ivory/50 mb-4">
            Nenhuma categoria encontrada
          </p>
          <Button onClick={() => handleOpenDialog()} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Adicionar primeira categoria
          </Button>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-brand-ink border-brand-gold/20">
          <DialogHeader>
            <DialogTitle className="text-brand-gold">
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-brand-ivory/60 mb-1 block">
                Nome *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Champagnes, Vinhos, Destilados..."
                required
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
              />
            </div>

            <div>
              <label className="text-sm text-brand-ivory/60 mb-1 block">
                Descrição
              </label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição da categoria"
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
              />
            </div>

            <div>
              <label className="text-sm text-brand-ivory/60 mb-1 block">
                Cor
              </label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full h-10 rounded-lg bg-brand-smoke/50 border border-brand-gold/20 cursor-pointer"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" variant="premium">
                {editingCategory ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
